import type { Answers, Groups } from "../../../../features/questionnaire";
import { isNoCoverValue } from "../../Section/Question/QuestionFactory/questions/PersonalisedCoverQuestion/constants";
import type { PartialQuestion, VisibilityMap } from "./types";

export type ShouldValidateNonVisibleParams = {
  question: PartialQuestion;
  groups?: Groups;
  answers?: Answers;
  visibilityMap?: VisibilityMap;
  serverVisibility?: Record<string, boolean>;
};

type PersonalisedCoverChildParams = {
  primaryQuestionName: string;
  questionVisible: boolean | undefined;
  answers: Answers;
};

/**
 * Check if a personalised cover child question should be validated when hidden in the visibilityMap.
 * Child questions should validate when:
 * - Cover is selected (primary answer is not a "no cover" value)
 * - The question itself is visible (by Chopin)
 */
const shouldValidatePersonalisedCoverChild = ({
  primaryQuestionName,
  questionVisible,
  answers,
}: PersonalisedCoverChildParams): boolean => {
  const primaryAnswer = answers[primaryQuestionName];

  // Check if cover is selected (answer is not a "no cover" value)
  const hasCover = primaryAnswer && !isNoCoverValue(primaryAnswer);

  // Validate if cover is selected and the question itself is visible (by Chopin)
  return !!hasCover && !!questionVisible;
};

type AddressLookupChildParams = {
  controllerQuestionName: string;
  questionVisible: boolean | undefined;
  questionRequired: boolean | undefined;
  visibilityMap?: VisibilityMap;
};

/**
 * Check if an address lookup child question should be validated when hidden in the visibilityMap.
 * Child questions should validate when:
 * - The controller (shadow) question is visible
 * - The child question is required
 * - The question itself is visible (by Chopin)
 */
const shouldValidateAddressLookupChild = ({
  controllerQuestionName,
  questionVisible,
  questionRequired,
  visibilityMap,
}: AddressLookupChildParams): boolean => {
  // Check if the controller question is visible
  const isControllerVisible = visibilityMap?.[controllerQuestionName] !== false;

  // Validate if controller is visible, the child is required, and the question itself is visible (by Chopin)
  return isControllerVisible && !!questionRequired && !!questionVisible;
};

/**
 * Check if a question should be validated despite having visibilityMap[name] === false.
 * Handles:
 * - Personalised cover child questions (when accordion is collapsed)
 * - Address lookup child questions (when fields are hidden until address is selected)
 */
export const shouldValidateNonVisible = ({
  question,
  groups,
  answers,
  visibilityMap,
  serverVisibility,
}: ShouldValidateNonVisibleParams): boolean => {
  if (!groups || !answers) return false;

  const questionName = question.name;
  // Use serverVisibility as the source of truth for the server-provided visible flag,
  // since question.visible may have been stamped with a computed value by processQuestionnaire.
  const questionVisible = serverVisibility?.[questionName] ?? question.visible;
  const questionRequired = question.required || question.isRequired;

  // Check personalised cover groups first (takes priority)
  const personalisedCoverEntry = Object.entries(groups).find(
    ([, group]) =>
      ["personalised_cover", "shadow_personalised_cover"].includes(
        group.kind,
      ) && group.names.includes(questionName),
  );

  if (personalisedCoverEntry) {
    const [primaryQuestionName] = personalisedCoverEntry;
    return shouldValidatePersonalisedCoverChild({
      primaryQuestionName,
      questionVisible,
      answers,
    });
  }

  // Check address lookup groups
  const addressGroupEntry = Object.entries(groups).find(
    ([, group]) =>
      group.kind === "address" && group.names.includes(questionName),
  );

  if (addressGroupEntry) {
    const [controllerQuestionName] = addressGroupEntry;
    return shouldValidateAddressLookupChild({
      controllerQuestionName,
      questionVisible,
      questionRequired,
      visibilityMap,
    });
  }

  // Question is not in any special group that needs validation when hidden
  return false;
};
